import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download, FileWarning, Lock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { ErrorNote, Spinner } from '@/components/ui/misc'
import { api, ApiError, type PublicShare } from '@/lib/api'
import { formatBytes, formatDateTime } from '@/lib/format'

/**
 * The page a share link opens. Deliberately outside the authenticated part of
 * the app: no session, no navigation into the rest of the tool, and nothing
 * shown beyond the single file being shared.
 */
export function SharePage() {
  const { token = '' } = useParams()
  const [share, setShare] = useState<PublicShare | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')

  useEffect(() => {
    api
      .publicShare(token)
      .then(setShare)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'This link is not valid.'),
      )
      .finally(() => setLoading(false))
  }, [token])

  function download() {
    window.location.href = api.publicDownloadUrl(token, password || undefined)
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <div className="rounded-lg border border-border bg-surface p-6">
          {loading && (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6" />
            </div>
          )}

          {error && (
            <div className="space-y-3 text-center">
              <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="font-medium">Link unavailable</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          )}

          {share && !share.available && (
            <div className="space-y-3 text-center">
              <FileWarning className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="font-medium">Link unavailable</p>
              <p className="text-sm text-muted-foreground">{share.problem}</p>
            </div>
          )}

          {share?.available && (
            <div className="space-y-5">
              <div className="space-y-1 text-center">
                <p className="text-sm text-muted-foreground">You have been sent a file</p>
                <p className="break-words text-lg font-semibold">{share.filename}</p>
                {share.size !== null && (
                  <p className="text-sm text-muted-foreground">{formatBytes(share.size)}</p>
                )}
                {share.label && (
                  <p className="pt-1 text-sm text-muted-foreground">{share.label}</p>
                )}
              </div>

              {share.requiresPassword && (
                <Field label="Password" htmlFor="share-password">
                  <Input
                    id="share-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Required for this link"
                    autoComplete="off"
                  />
                </Field>
              )}

              <Button
                variant="primary"
                className="w-full justify-center"
                onClick={download}
                disabled={share.requiresPassword && password.length === 0}
              >
                <Download className="h-4 w-4" />
                Download
              </Button>

              <div className="space-y-1 border-t border-border pt-4 text-xs text-muted-foreground">
                {share.expiresAt && <p>Link expires {formatDateTime(share.expiresAt)}</p>}
                {share.downloadsRemaining !== null &&
                  share.downloadsRemaining !== undefined && (
                    <p>
                      {share.downloadsRemaining} download
                      {share.downloadsRemaining === 1 ? '' : 's'} remaining
                    </p>
                  )}
                {share.requiresPassword && (
                  <p className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Password protected
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {share?.available && (
          <ErrorNote>
            Downloads through this link are recorded. Do not forward it to anyone who should
            not have the file — anyone holding the URL can download it.
          </ErrorNote>
        )}
      </div>
    </div>
  )
}
